import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CustomLoginService } from '@privatelib/ngx18-authz-dev';
import { BehaviorSubject, Observable, finalize, map, tap, zip } from 'rxjs';
import { jwtDecode } from "jwt-decode";
import { API_ROUTES, ROUTE_CONSTANTS } from '../../util/route.constants';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FetchFacilityResponse } from '../../models/facility.interface';
import { SessionInactivityService } from '../session-inactivity/session-inactivity.service';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { CVANotificationService } from '../cva-notification/cva-notification.service';
import { hasAuthTokens } from '../../util/auth-tokens.util';
export interface PractitionerPayload {
  providerId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  phoneNo?: string;
  company?: string;
  id: string;
}

export interface IRefreshTokenResponse {
  authToken: string,
  idToken: string,
  refreshToken: string
}

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  loggedIn$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  selectedFacility = new BehaviorSubject<string>('');
  selectedFacility$ = this.selectedFacility.asObservable();
  userSubject: BehaviorSubject<PractitionerPayload | null> = new BehaviorSubject<PractitionerPayload | null>(null);

  private readonly AUTH_TOKEN_KEY = 'authToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly ID_TOKEN_KEY = 'idToken';


  constructor(
    private readonly loginService: CustomLoginService,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly sessionInactivityService: SessionInactivityService,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly dialog: MatDialog,
  ) { }

  isLoggedIn() {
    return zip([this.loginService.verifyIdToken(),
    this.loginService.verifyAuthorizationToken()
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    ]).pipe(map((res: any[]) => {
      if (res[0].status === 200 && res[1].status === 200 && hasAuthTokens()) {
        return true;
      }
      return false;
    })
    )
  }

  logout() {
    this.loginService.logout().subscribe({
      next: () => {
        this.revokeOnLogout(true);
      },
      error: (err: Error) => {
        console.error(err);
        this.revokeOnLogout(true);
      }
    })
  }

  getMappedFacilities(providerId: string): Observable<FetchFacilityResponse> {
    const url = `${environment.user_management_api_url}${API_ROUTES.LIST_FACILITY}/${providerId}`;

    return this.http.get<FetchFacilityResponse>(url);
  }

  onPartialLogout(): Promise<boolean> {
    return new Promise((resolve) => {
      this.loginService.logout(false)
        .pipe(
          finalize(() => {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            this.loggedIn$.next(false);
            this.dialog.closeAll();
          })
        )
        .subscribe({
          next: () => resolve(true),
          error: () => resolve(true) // ignore the error
        });
    });
  }

  revokeOnLogout(inDirectSignout: boolean = false, reason?: string) {
    const isLIC = localStorage.getItem('isLIC') === 'true';
    this.userSubject.next(null);
    this.loggedIn$.next(false);
    localStorage.clear();
    sessionStorage.clear();
    this.sessionInactivityService.stopOnLogout();
    this.dialog.closeAll();

    if (inDirectSignout) {
      if (reason) {
        this.cvaNotificationService.showNotification(reason, NotificationClassEnum.ERROR);
      }

      if (isLIC) {
        this.router.navigate([ROUTE_CONSTANTS.LIC_SESSION_EXPIRED]);
      } else {
        this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
      }
    }
  }

  redirectToResetPassword(redirectUrl: string) {
    redirectUrl += `&reason=expired`;
    const currentOrigin = window.location.origin;

    try {
      const url = new URL(redirectUrl);
      if (url.origin === currentOrigin) {
        // Internal route
        const relativePath = url.pathname + url.search + url.hash;
        this.navigateByUrl(relativePath);
      } else {
        // External URL
        this.goTo(redirectUrl);
      }
    } catch (err) {
      console.error('Invalid redirect URL:', err);
    }
  }

  goTo(url: string) {
    window.location.href = url;
  }

  navigateByUrl(redirectUrl: string) {
    this.router.navigateByUrl(redirectUrl);
  }

  // Decode the JWT token and extract user information
  decodeToken(token: string): PractitionerPayload {
    return jwtDecode<PractitionerPayload>(token);
  }


  refreshToken(): Observable<IRefreshTokenResponse> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY) || '';

    return this.http.post<IRefreshTokenResponse>(
      `${environment.user_management_api_url}${API_ROUTES.REFRESH_TOKEN}`,
      { refreshToken }
    ).pipe(
      tap((response: IRefreshTokenResponse) => {
        if (response?.idToken) {
          this.setIDToken(response.idToken);
        }

        if (response?.authToken) {
          this.setAuthToken(response.authToken);
        }

        if (response?.refreshToken) {
          this.setRefreshToken(response.refreshToken);
        }
      })
    );
  }

  setIDToken(token: string) {
    localStorage.setItem(this.ID_TOKEN_KEY, token);
  }

  setAuthToken(token: string): void {
    localStorage.setItem(this.AUTH_TOKEN_KEY, token);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  sessionLogin(payload: { email: string, password: string, token: string, action: string }): Observable<{ redirectUrl: string }> {
    return this.http.post<{ redirectUrl: string }>(`${environment.user_management_api_url}${API_ROUTES.SESSION_LOGIN}`, payload)
  }

  validateLoginOtp(email: string, otp: string): Observable<{ redirectUrl: string }> {
    return this.http.post<{ redirectUrl: string }>(
      `${environment.user_management_api_url}${API_ROUTES.VALIDATE_LOGIN_OTP}`,
      { email, otp }
    );
  }
}
